// ════════════════════════════════════════════════════════════════════════════
// components/Cotizaciones/CotizacionesList.jsx
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Chip,
  InputAdornment, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Tooltip, Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  FileDownload as DownloadIcon,
  FilterList as FilterIcon,
  AdsClick as TargetIcon, // Icono de Origen similar al de tu imagen
  Edit as EditIcon,
  Autorenew as ConvertIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getCotizaciones } from '../../services/crmService';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import api from '../../services/api';
import { useFiscal } from '../../hooks/useFiscal';

const ESTADOS = {
  contacto_inicial: { label: 'Contacto Inicial', color: '#f59e0b' }, // Naranja
  propuesta_enviada: { label: 'Propuesta Enviada', color: '#f59e0b' }, // Naranja
  cerrado_ganado: { label: 'Ganado', color: '#10b981' }, // Verde
  cerrado_perdido: { label: 'Perdido', color: '#ef4444' }
};

const CotizacionesList = () => {
  const fiscal = useFiscal();
  const navigate = useNavigate();
  const [cotizaciones, setCotizaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, cotizaciones_rapidas: 0, oportunidades_crm: 0 });
  const [filtros, setFiltros] = useState({ search: '', estado: '', origen: '' });

  useEffect(() => {
    cargarCotizaciones();
  }, [filtros]);

  const cargarCotizaciones = async () => {
    setLoading(true);
    try {
      const response = await getCotizaciones(filtros);
      if (response.data?.success) {
        setCotizaciones(response.data.data || []);
        setStats(response.data.stats || stats);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const formatearMonto = (monto) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: fiscal.moneda?.codigo || 'DOP',
    }).format(monto || 0);
  };

  const handleVerPDF = (cotizacion) => {
  navigate(`/cotizaciones/${cotizacion.id}`);
};

const handleDescargarPDF = (cotizacion) => {
navigate(`/cotizaciones/${cotizacion.id}`, { state: { autoPrint: true } });
};

  const handleConvertir = async (id) => {
  // 1. Buscamos la cotización específica en tu lista actual para obtener sus datos
  const cotizacionActual = cotizaciones.find(c => c.id === id);
  
  if (!cotizacionActual) {
    toast.error("No se encontraron los datos de la cotización");
    return;
  }

  if (window.confirm(`¿Deseas convertir la cotización de ${cotizacionActual.Prospecto?.nombre || 'este cliente'} en una Oportunidad?`)) {
    try {
      // 2. Construimos el objeto con la estructura que tu DB PostgreSQL exige
      const datosParaOportunidad = {
        prospecto_id: cotizacionActual.prospecto_id,
        etapa: 'propuesta_enviada', // IMPORTANTE: Así aparecerá en el Kanban
        valor_estimado: cotizacionActual.total, // Columna numeric(15,2)
        
        // Alimentamos el campo JSONB 'propuesta' que vimos en tu tabla
        propuesta: {
          total: cotizacionActual.total,
          subtotal: cotizacionActual.subtotal,
          items: cotizacionActual.partidas || [],
          fecha_conversion: new Date()
        },
        
        // Datos extra para que el Pipeline sea más profesional
        probabilidad: 50,
        notas: `Convertido automáticamente desde Cotización #${id}`
      };

      console.log("🚀 Enviando datos de conversión:", datosParaOportunidad);

      // 3. Enviamos el ID y los DATOS (esto quita el error 400)
      const response = await api.post(`/cotizaciones/${id}/convertir`, datosParaOportunidad);

      if (response.data.success) {
        toast.success("¡Cotización convertida y visible en el Pipeline!");
        cargarCotizaciones(); 
      }
    } catch (error) {
      console.error("❌ Error en conversión:", error.response?.data);
      toast.error(error.response?.data?.error || "Error al procesar la conversión");
    }
  }
};

  return (
    <Box sx={{ p: 4, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      
      {/* HEADER: Título y Botón Azul */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: '500', mb: 0.5 }}>
            📄 Cotizaciones
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestión de cotizaciones y propuestas comerciales
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/cotizaciones/nueva')}
          sx={{ 
            bgcolor: '#1976d2', 
            textTransform: 'none', 
            fontWeight: 'bold',
            px: 3,
            borderRadius: '4px'
          }}
        >
          NUEVA COTIZACIÓN
        </Button>
      </Box>

      {/* FILTROS: Diseño exacto de la imagen */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Buscar por cliente, email..."
          value={filtros.search}
          onChange={(e) => setFiltros({ ...filtros, search: e.target.value })}
          sx={{ bgcolor: 'white', width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ width: 180, bgcolor: 'white' }}>
          <InputLabel>Estado</InputLabel>
          <Select
            value={filtros.estado}
            label="Estado"
            onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
          >
            <MenuItem value="">Todos</MenuItem>
            {Object.entries(ESTADOS).map(([key, value]) => (
              <MenuItem key={key} value={key}>{value.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <IconButton sx={{ border: '1px solid #ced4da', borderRadius: '4px', bgcolor: 'white' }}>
          <FilterIcon fontSize="small" />
        </IconButton>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={cargarCotizaciones}
          sx={{ 
            textTransform: 'none', 
            borderColor: '#ced4da', 
            color: '#1976d2',
            bgcolor: 'white'
          }}
        >
          ACTUALIZAR
        </Button>
      </Stack>

      {/* TABLA: Estilo de la imagen con bordes redondeados y sombreado suave */}
      <TableContainer component={Paper} sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f8f9fa' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', color: '#5f6368', py: 1.5 }}>No. Cotización</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#5f6368' }}>Cliente / Prospecto</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#5f6368' }}>Total</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#5f6368', textAlign: 'center' }}>Origen</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#5f6368', textAlign: 'center' }}>Estado</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#5f6368', textAlign: 'right', pr: 4 }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5 }}><CircularProgress size={30} /></TableCell></TableRow>
            ) : (
              cotizaciones.map((cot) => {
                const config = ESTADOS[cot.estado] || ESTADOS.contacto_inicial;
                return (
                  <TableRow key={cot.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    {/* No. Cotización en Azul */}
                    <TableCell sx={{ color: '#1976d2', fontWeight: '500' }}>
                      {cot.numero_cotizacion}
                    </TableCell>

                    {/* Cliente: Nombre arriba, Empresa abajo */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: '500' }}>{cot.cliente?.nombre}</Typography>
                      <Typography variant="caption" color="text.secondary">{cot.cliente?.empresa}</Typography>
                    </TableCell>

                    {/* Total en Negrita */}
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {formatearMonto(cot.total)}
                    </TableCell>

                    {/* Origen: Icono de Target */}
                    <TableCell align="center">
                      <TargetIcon sx={{ color: '#e57373', fontSize: 20 }} />
                    </TableCell>

                    {/* Estado: Chips redondeados con borde (Igual a la imagen) */}
                    <TableCell align="center">
                      <Chip
                        label={config.label}
                        size="small"
                        sx={{
                          bgcolor: alpha(config.color, 0.05),
                          color: config.color,
                          border: `1px solid ${config.color}`,
                          fontWeight: '500',
                          fontSize: '0.75rem',
                          borderRadius: '12px',
                          px: 1
                        }}
                      />
                    </TableCell>
{/* Acciones: PDF, Download, Editar y Convertir */}
<TableCell align="right" sx={{ pr: 3 }}>
  <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
    
    {/* Ver/PDF */}
    <Tooltip title="Ver Detalle / PDF">
    <IconButton size="small" color="primary" onClick={() => handleVerPDF(cot)}>
        <PdfIcon sx={{ color: '#1976d2', fontSize: 18 }} />
      </IconButton>
    </Tooltip>


    {/* EDITAR: Te lleva de vuelta al formulario con los datos */}
    <Tooltip title="Editar Cotización">
      <IconButton 
        size="small" 
      onClick={() => navigate(`/cotizaciones/editar/${cot.id}`)}
        sx={{ color: '#5f6368' }}
      >
        <EditIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>

    {/* CONVERTIR: Botón de acción para pasar a Oportunidad */}
    {!cot.convertida_a_oportunidad && (
      <Button
        variant="contained"
        size="small"
        startIcon={<RefreshIcon sx={{ fontSize: '14px !important' }} />}
        onClick={() => handleConvertir(cot.id)}
        sx={{ 
          bgcolor: '#10b981', 
          '&:hover': { bgcolor: '#059669' },
          fontSize: '0.65rem',
          textTransform: 'none',
          borderRadius: '12px',
          height: '24px',
          ml: 1
        }}
      >
        Convertir
      </Button>
    )}
  </Stack>
</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CotizacionesList;